import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ModeratAnfrage } from '../anfrage/moderat.anfrage';
import {ResultMeasure} from "../interfaces/result-data.interface";

/**
 * Component for showing the list of possible measures on the moderat result page.
 */
@Component({
    selector: 'app-sanierung-measures',
    templateUrl: './sanierung-measures.component.html',
    standalone: false
})
export class SanierungMeasuresComponent implements OnChanges {
  /**
   * Map with data for each measure option (e.g. dach, fassade, heating variants).
   */
  @Input() measureData: { [key: string]: ResultMeasure };

  /**
   * Grouped measure keys used to iterate sections in the template.
   */
  @Input() measures: { [key: string]: string[] };

  /**
   * toggle if the measure district heating is disabled
   */
  @Input() disableMeasureDistrictHeating?: boolean = false;

  /**
   * Reactive form instance containing controls for each measure selection.
   */
  @Input() form: UntypedFormGroup;

  /**
   *  Signals if the portal is an internal deployment. Affects some visual details.
   */
  @Input() internalPortal: boolean;

  /**
   *  The complete request object, needed to compute text postfixes based on energy source.
   */
  @Input() anfrage: ModeratAnfrage;

  /**
   * Loading flag to know when data updates finished to (re)compute maxSaving.
   */
  @Input() loading: boolean;

  /**
   * Emits when a measure row toggles its detailed text, so the parent can recalc layout (e.g. side button offset).
   */
  @Output() toggleText = new EventEmitter<void>();

  /**
   * Locally computed maximum saving (kWh) used for relative bar widths. Negative number or 0.
   */
  maxSaving: number = 0;

  /**
   * Whether the unit help callout (kWh explanation) is currently visible. Managed locally.
   *
   */
  showUnitHelp: boolean = false;

  /**
   * Forward handler for child measure components to notify parent about text toggle events.
   */
  onToggleText(): void {
    this.toggleText.emit();
  }

  /**
   * Toggle visibility of the unit help callout.
   *
   */
  toggleUnitHelp(): void {
    this.showUnitHelp = !this.showUnitHelp;
  }

  /**
   * Close the unit help callout.
   */
  closeUnitHelp(): void {
    this.showUnitHelp = false;
  }

  /**
   * Triggered when inputs change to recompute local maxSaving when data is available.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.measureData || changes.loading) {
      this.recomputeMaxSaving();
    }
  }

  /**
   * Recompute the maximum kWh saving (most negative value; 0 if none).
   */
  private recomputeMaxSaving(): void {
    let max = 0;
    if (this.measureData) {
      Object.keys(this.measureData).forEach(key => {
        const val = this.measureData[key]?.saving;
        if (typeof val === 'number') {
          max = Math.min(max, val);
        }
      });
    }
    this.maxSaving = max;
  }

  /**
   * Compute a translation postfix for certain heating options.
   * For 'erneuern' and 'nicht_erneuern' we append '.gasoel' if the current energy source is fossil (erdgas, heizoel, fluessiggas).
   */
  getTextPostfix(measure: string): string {
    switch (measure) {
      case 'erneuern':
      case 'nicht_erneuern':
        const carrier = this.anfrage?.verbrauchsanalyseInput?.energietraeger_name;
        const isFossil = ['erdgas', 'heizoel', 'fluessiggas'].indexOf(carrier) > -1;
        return isFossil ? '.gasoel' : '';
    }
    return '';
  }

  /**
   * Changes the displayed measure name based on the provided measure parameter.
   * If the measure has a construction type, the measure name is formatted accordingly.
   *
   * @param {string} measure - The identifier for the measure to format.
   * @return {string} The formatted measure name or the original measure if no specific condition is met.
   */
  getMeasureName(measure: string): string {
    if ((measure === 'fenster1' || measure === 'fenster2') && this.measureData[measure]?.constructionType) {
      return 'fenster_' + this.measureData[measure].constructionType;
    }
    return measure;
  }

  /**
   * Returns the area percentage for fenster1/fenster2 to display in the measure name.
   * Returns null for all other measures.
   */
  getWindowPercentage(measure: string): string | null {
    if (measure === 'fenster1') {
      const percent = this.anfrage?.bestandsanalyseInput?.fenster_flaechenanteil;
      return percent != null ? percent : null;
    }
    if (measure === 'fenster2') {
      const percent = this.anfrage?.bestandsanalyseInput?.fenster_flaechenanteil_2;
      return percent != null ? percent : null;
    }
    return null;
  }

  /**
   * toggle if measure district heating is disabled in case of district heating path
   * @param measure
   */
  isMeasureDistrictDisabled(measure: string): boolean {
    return measure === 'fernwaerme' && this.disableMeasureDistrictHeating;
  }
}
